import React, { useRef, useCallback } from "react";
import {
  Bold,
  Italic,
  Strikethrough,
  Link,
  Quote,
  Code,
  List,
  ListOrdered,
} from "lucide-react";

const TOOLBAR_ACTIONS = [
  { icon: Bold, wrap: ["**", "**"], placeholder: "bold text", title: "Bold" },
  {
    icon: Italic,
    wrap: ["*", "*"],
    placeholder: "italic text",
    title: "Italic",
  },
  {
    icon: Strikethrough,
    wrap: ["~~", "~~"],
    placeholder: "strikethrough",
    title: "Strikethrough",
  },
  "divider",
  {
    icon: Link,
    wrap: ["[", "](url)"],
    placeholder: "link text",
    title: "Link",
  },
  { icon: Quote, prefix: "> ", placeholder: "quote", title: "Quote" },
  { icon: Code, wrap: ["`", "`"], placeholder: "code", title: "Code" },
  "divider",
  { icon: List, prefix: "- ", placeholder: "list item", title: "Bullet list" },
  {
    icon: ListOrdered,
    prefix: "1. ",
    placeholder: "list item",
    title: "Numbered list",
  },
];

function insertAtCursor(textarea, action, currentValue) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selected = currentValue.slice(start, end);

  let newText;
  let newCursorStart;
  let newCursorEnd;

  if (action.wrap) {
    const [before, after] = action.wrap;
    const inner = selected || action.placeholder;
    newText =
      currentValue.slice(0, start) +
      before +
      inner +
      after +
      currentValue.slice(end);
    newCursorStart = start + before.length;
    newCursorEnd = newCursorStart + inner.length;
  } else if (action.prefix) {
    const lineStart = currentValue.lastIndexOf("\n", start - 1) + 1;
    const inner = selected || action.placeholder;
    newText =
      currentValue.slice(0, lineStart) +
      action.prefix +
      currentValue.slice(lineStart, start) +
      inner +
      currentValue.slice(end);
    const offset = action.prefix.length;
    newCursorStart = start + offset;
    newCursorEnd = newCursorStart + inner.length;
  }

  return { newText, newCursorStart, newCursorEnd };
}

export default function MarkdownEditor({
  value,
  setValue,
  onBlur,
  height = null,
}) {
  const textareaRef = useRef(null);

  const handleChange = useCallback(
    (e) => {
      setValue?.(e.target.value);
    },
    [setValue],
  );

  const handleToolbarClick = useCallback(
    (action) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const { newText, newCursorStart, newCursorEnd } = insertAtCursor(
        textarea,
        action,
        value ?? "",
      );

      setValue?.(newText);

      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorStart, newCursorEnd);
      });
    },
    [value, setValue],
  );

  return (
    <div className="border border-[#E9EAEB80] dark:border-gray-800 focus-within:border-secondary focus-within:ring-1 focus-within:ring-secondary rounded-lg overflow-hidden bg-white dark:bg-gray-900 transition-colors">
      <div className="flex items-center gap-0.5 px-3 py-2 border-b border-[#E9EAEB80] dark:border-gray-800 bg-[#FAFAFA] dark:bg-gray-800">
        {TOOLBAR_ACTIONS.map((action, i) =>
          action === "divider" ? (
            <div key={`d-${i}`} className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />
          ) : (
            <button
              key={action.title}
              type="button"
              title={action.title}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleToolbarClick(action)}
            >
              <action.icon size={16} strokeWidth={2} />
            </button>
          ),
        )}
      </div>

      <textarea
        ref={textareaRef}
        value={value ?? ""}
        onChange={handleChange}
        onBlur={onBlur}
        className="w-full p-4 text-xs text-gray-900 dark:text-gray-100 leading-6 resize-none outline-none bg-white dark:bg-gray-900 font-sans"
        style={height ? { minHeight: height } : { minHeight: "420px" }}
        placeholder="Write your content here (Markdown supported)..."
        spellCheck={false}
      />
    </div>
  );
}
